import React from 'react'
import PropTypes from 'prop-types'
import exact from 'prop-types-exact'
import * as Yup from 'yup'
import { StepForm, HouseholdMembersFormFields } from 'forms'
import { INITIAL_FORM_VALUES } from 'config'
import { FieldArray } from 'formik'
import uuid from 'react-uuid'
import { parse } from 'date-fns'

const propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  certType: PropTypes.string,
  fundingProgram: PropTypes.string,
}

const defaultProps = {
  certType: null,
  fundingProgram: null,
}

function buildValidationSchema({ hideSpecialQuestions, hideCitizenshipQuestion }) {
  return Yup.object({
    householdMembers: Yup.array().of(
      Yup.object({
        firstName: Yup.string().required('First name is required'),
        lastName: Yup.string().required('Last name is required'),
        relationship: Yup.string().required('Relationship is required'),
        dateOfBirth: Yup.date()
          .max(new Date(), 'You must be at least one day old')
          .required('Date of birth is required')
          .min(new Date('01/01/1900'), 'Invalid date')
          .transform((_, origValue) => {
            return parse(origValue, 'MM/dd/yyyy', new Date())
          }),
        student: Yup.boolean().required('Student status is required'),
        disabled: Yup.boolean().required('Disability status is required'),
        ssn: Yup.string()
          .matches(/^\d+$/, 'SSN must be valid')
          .length(9, 'SSN must be valid')
          .required('SSN is required'),
        income: Yup.boolean().required('Income status is required'),
        race: Yup.string().required('Race is required'),
        ethnicity: Yup.string().required('Ethnicity is required'),
        gender: Yup.string().required('Gender is required'),
        eligibleCitizen: hideCitizenshipQuestion
          ? Yup.string().optional()
          : Yup.string().required('Citizenship status is required'),
        eligibleCitizenDetails: hideCitizenshipQuestion
          ? Yup.string().optional()
          : Yup.string().when('eligibleCitizen', {
              is: (value) => value !== 'Yes',
              then: () =>
                Yup.string().required('Eligible citizen details are required'),
            }),
        livedInOtherStates: hideSpecialQuestions
          ? Yup.string().optional()
          : Yup.string().required('Lived in other states is required'),
        livedInOtherStatesDetails: hideSpecialQuestions
          ? Yup.string().optional()
          : Yup.string().when('livedInOtherStates', {
              is: (value) => value == 'Yes',
              then: () =>
                Yup.string().required(
                  'Lived in other states details are required'
                ),
            }),
        taxFilingStatus: hideSpecialQuestions
          ? Yup.string().optional()
          : Yup.string().required('Tax filing status is required'),
        maritalStatus: Yup.string().required('Marital status is required'),
        maritalStatusDetails: Yup.string().when('maritalStatus', {
          is: (value) => !['Separated', 'Estranged', 'Single'].includes(value),
          then: () =>
            Yup.string().required('Marital status details are required'),
        }),
        separationDate: Yup.date().when('maritalStatus', {
          is: (value) => ['Separated', 'Estranged'].includes(value),
          then: () =>
            Yup.date()
              .required('Separation date is required')
              .max(new Date(), 'Separation date cannot be in the future'),
        }),
        isSeparatedSpousePartOfHousehold: Yup.boolean().when('maritalStatus', {
          is: (value) => ['Separated', 'Estranged'].includes(value),
          then: () =>
            Yup.boolean().required(
              'Is the spouse currently part of the household question is required'
            ),
        }),
        isEngagedInLegalSeparationAction: Yup.boolean().when('maritalStatus', {
          is: (value) => ['Separated', 'Estranged'].includes(value),
          then: () =>
            Yup.boolean().required(
              'Is this household member currently engaged in the process of legal separation or divorce question is required'
            ),
        }),
        understandHalfOfJointAssets: Yup.boolean().when('maritalStatus', {
          is: (value) => ['Separated', 'Estranged'].includes(value),
          then: () =>
            Yup.boolean().required(
              'Do you understand that 50% of any joint assets held with your spouse will counted as part of your assets/income question is required'
            ),
        }),
        understandRequiredToReportChanges: Yup.boolean().when('maritalStatus', {
          is: (value) => ['Separated', 'Estranged'].includes(value),
          then: () =>
            Yup.boolean().required(
              'Do you understand that you are required to report any changes to household composition, income, assets, or marital status is required'
            ),
        }),
        understandNewMembersAreToBReported: Yup.boolean().when('maritalStatus', {
          is: (value) => ['Separated', 'Estranged'].includes(value),
          then: () =>
            Yup.boolean().required(
              'Do you understand that adding any new member to your household, including your current spouse, requires prior approval from management is required'
            ),
        }),
        taxRefundReceived: hideSpecialQuestions
          ? Yup.string().optional()
          : Yup.string().required('Tax refund received is required'),
        taxRefundReceivedAmount: hideSpecialQuestions
          ? Yup.string().optional()
          : Yup.string().when('taxRefundReceived', {
              is: (value) => value == 'Yes',
              then: () =>
                Yup.string().required(
                  'Please provide the amount of the tax refund received'
                ),
            }),
      })
    ),
  })
}

function HouseholdMembersForm({
  onSubmit,
  initialValues,
  certType,
  fundingProgram,
}) {
  const isLIHTC = fundingProgram && fundingProgram.includes('LIHTC')
  const isHUD = fundingProgram && fundingProgram.includes('HUD')

  // livedInOtherStates, taxFilingStatus, taxRefundReceived: LIHTC only
  const hideSpecialQuestions = !isLIHTC
  // eligibleCitizen: LIHTC or HUD
  const hideCitizenshipQuestion = !isLIHTC && !isHUD

  const validationSchema = buildValidationSchema({ hideSpecialQuestions, hideCitizenshipQuestion })

  return (
    <StepForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      hidePreviousButton
    >
      {({ values, setFieldValue }) => (
        <FieldArray name="householdMembers">
          {({ push, remove }) => (
            <>
              {values.householdMembers.map(
                (
                  {
                    id,
                    key,
                    ssn,
                    maritalStatus,
                    eligibleCitizen,
                    livedInOtherStates,
                    taxRefundReceived,
                  },
                  index
                ) => {
                  const heading =
                    index > 0
                      ? `Person #${index + 1} Information`
                      : 'Your Information'
                  return (
                    <div key={id ?? key} className="form-section">
                      <div className="heading-wrapper">
                        <h2>{heading}</h2>
                        {index > 0 && (
                          <button
                            type="button"
                            className="link-warn remove-icon"
                            onClick={() => remove(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <HouseholdMembersFormFields
                        index={index}
                        setFieldValue={setFieldValue}
                        currentSsnValue={ssn}
                        currentMaritalStatusValue={maritalStatus}
                        currentEligibleCitizenValue={eligibleCitizen}
                        currentLivedInOtherStatesValue={livedInOtherStates}
                        currentTaxRefundReceivedValue={taxRefundReceived}
                        hideSpecialQuestions={hideSpecialQuestions}
                        hideCitizenshipQuestion={hideCitizenshipQuestion}
                      />
                    </div>
                  )
                }
              )}
              <button
                type="button"
                className="link-primary add-icon"
                onClick={() =>
                  push({
                    ...INITIAL_FORM_VALUES.HOUSEHOLD_MEMBERS,
                    key: uuid(),
                  })
                }
              >
                Add Another Person
              </button>
            </>
          )}
        </FieldArray>
      )}
    </StepForm>
  )
}

HouseholdMembersForm.propTypes = exact(propTypes)
HouseholdMembersForm.defaultProps = defaultProps

export default React.memo(HouseholdMembersForm)
